import React from 'react';
import Head from 'next/head';
import List from '@mui/material/List';
import HelpIcon from '@mui/icons-material/Help';
import ListItem from '@mui/material/ListItem';
import SearchIcon from '@mui/icons-material/Search';
import Accordion from '@mui/material/Accordion';
import styles from '../styles/SearchTips.module.css';
import { useScreenSize } from '../hooks/useScreenSize';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

function SearchTips() {

  const { size } = useScreenSize();
  const mobile = size === 'xxs' || size === 'xs' || size === 'sm' ? true : false;

  return (
    <>
      <Head>
        <title>Search Tips | Fitness and Exercise Equipment Repair Parts</title>
        <meta
          name="description"
          content={'Tips on quickly and accurately locating the parts you need for your fitness equipment.'}
        />
        <meta httpEquiv="content-language" content="en-us" />
      </Head>
        <div className='row'>
          <div className={styles.articleInner}>
            <h1 className={styles.pageTitle}>HOW TO FIND PARTS</h1>
          </div>
        </div>
        <div className={styles.stepsRow}>
          <div className={styles.stepContainer}>
            <img src="/images/tips_model.jpg" height={155} width={300} style={{ margin: 'auto' }} />
            <h1>1. Know Your Model Number</h1>
            <p style={{ textAlign: 'center' }}>
              The ONLY way to ensure that you are getting the correct parts is to have your equipment&apos;s model number.
              <br />
              <a href="#find-model-number">
                How do I find my equipment&apos;s model number?
              </a>
            </p>
          </div>
          <div className={styles.stepContainer}>
            <img src="/images/tips_searchbar.jpg" height={155} width={300} style={{ margin: 'auto' }} />
            <h1>2. Type Your Model into the Search Bar</h1>
            <p style={{ textAlign: 'center' }}>
              Enter your machine&apos;s model number into the search bar found at the very top, right-side of the page and click the search button.
              <br /><a href="#optimize-search">How to optimize my search?</a>
            </p>
          </div>
          <div className={styles.stepContainer}>
            <img src="/images/tips_diagram.jpg" height={155} width={300} style={{ margin: 'auto' }} />
            <h1>3. Explore Parts List or Exploded Diagram</h1>
            <p style={{ textAlign: 'center' }}>
              Equipment model pages list the parts for that machine <span>and</span> usually contain an exploded diagram of the machine. Search through this list or the diagram for the correct part.
            </p>
          </div>
        </div>
        <div className={styles.articleContainer}>
          <div className={styles.articleInner}>
            <h3 className={styles.articleTitle}>Before You Search: Know Your Model Number</h3>
            <p style={{ padding: 5 }}>
              Everything on our website is classified by the manufacturer&apos;s model information and parts list for the machine. The equipment models on our website display the parts that go with each machine, and the parts on our website display what equipment models they go with. In a few cases you may also need your equipment&apos;s <a href="#serial-numbers"> serial number</a>.<b> Make sure your part is listed with your model number before ordering!</b>
            </p>
          </div>
        </div>
        <div className={styles.articleContainer} id='find-model-number'>
          <div className={styles.articleInner}>
            <h3 className={styles.articleTitle}>Where To Find Your Model Number</h3>
            <p style={{ padding: 5 }}>
              Model information is generally found on a white or silver sticker located on the frame of the machine. This location changes depending on what type of machine you have and who the manufacturer is.
            </p>
          </div>
          <div className={styles.tipsContainer}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Treadmills
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                On the side of an upright or on the display console. Near where the power cord enters the machine. If it can be put in an upright position, it may be down along the frame of the machine.
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Ellipticals/Bikes
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                On the display console. Near where the power cord enters the machine. On the front or rear stabilizers. It may even be on the sides or underneath the machine.
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Steppers
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                On the display console. Near where the power cord enters the machine. On the bottom of the frame under the foot plate.
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Strength Machines
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                Underneath a seat (if the machine has one). On the frame of the machine near the base. Near the weight stack (if the machine has one).
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
        <div className={styles.articleContainer} id='optimize-search'>
          <div className={styles.articleInner}>
            <h3 className={styles.articleTitle}>When You Search: How To Find The Right Part</h3>
          </div>
          <div className={styles.tipsContainer}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Try to find your EQUIPMENT model page
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row' style={{ width: '100%' }}>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    This will take you to the exact parts list for your machine.
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: PETL61590
                    <br />
                    Example: 831.297801
                    <br />
                    Example: CENTL82520
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Type only your model number
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Typing just the model number into the search box will find a direct match.
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: &quot;<b>831.246462</b>&quot; instead of &quot;831.246462 xp 680&quot; or &quot;831.246462 display console&quot;
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Leave out other extra words
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Do not include extra words, including the parts you need, that can clutter your search for your equipment page.
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: &quot;<b>Proform 615</b>&quot; instead of &quot;Proform 615 lift frame&quot;
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Search for equipment model, not part type
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-12'>
                    We do not recommend simply searching for your part such as &quot;pulley&quot; or &quot;battery cover&quot;. We are working to make it as easy as possible to browse and filter through the &quot;Catalog Part Matches,&quot; but you will find the correct part much quicker if you can find the correct equipment page.
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                The last digit of the model number might be a version number
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Sometimes the last digit of a model number represents the version number and may be separated from the other numbers by a period. You do not need to include that ending period in your search but our system will understand what you are looking for either way.
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: &quot;WETL21023&quot; = &quot;WETL2102.3&quot;
                    <br />
                    Example: &quot;831.295032&quot; = &quot;831.29503.2&quot;
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                What if there is no model number?
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    If you don&apos;t have an exact model number, include your BRAND name in your search to help narrow your results.
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: &quot;<strong>Proform</strong> 615&quot; instead of &quot;615&quot;
                    <br />
                    Example: &quot;<strong>Bowflex</strong> ultimate&quot; instead of &quot;ultimate&quot;
                    <br />
                    Example: &quot;<strong>Schwinn</strong> airdyne&quot; instead of &quot;airdyne&quot;
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                What if you are not taken directly to an equipment page?
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-12' style={{ padding: 15 }}>
                    If your search does not take you directly to your equipment page, look for your equipment under &quot;Equipment Matches&quot; and ignore &quot;Catalog Part Matches&quot;. You may need to click &quot;View All Matches&quot; to see more results.
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                What if you cannot find your equipment page?
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    If you can&apos;t find your equipment page but have an exact manufacturer part number, you can search for that directly.
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: QQ2065
                    <br />
                    Example: KK-2044
                    <br />
                    Example: AK58-00322-0000
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
        <div className={styles.articleContainer} id='serial-numbers'>
          <div className={styles.articleInner}>
            <h3 className={styles.articleTitle}>What About Serial Numbers?</h3>
            <p style={{ padding: 5 }}>
              Manufacturers have no standard on how to catalog serial numbers, therefore our primary method of cataloging parts is by using an equipment&apos;s MODEL NUMBER. However,
              we will list serial number information alongside an equipment&apos;s model name whenever possible. If it exists, <b>serial number information will be displayed in
                parentheses behind an equipment&apos;s name.</b>
            </p>
          </div>
          <div className={styles.tipsContainer}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Serial codes are the first few letters/numbers of the equipment model&apos;s serial information
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    If the model contains a few letters or numbers in parentheses behind the name, this is the model&apos;s serial code. These are the starting characters of that model&apos;s serial number. There might be multiple serial codes for the same model.
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: <b>Precor - C546 Version 1 (4H, 5V, 5W)</b> means that the serial numbers for this model either begin with 4H, 5V or 5W
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Serial ranges show the value your serial number can fall between
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    If there is an &quot;S/N&quot; in the parentheses behind an equipment model&apos;s name, this is showing a range that the serial number will fall between.
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: <b>Cybex - 751T Treadmill (S/N D0402-D0507)</b> will have a serial number that begins with the letter &quot;D&quot; and falls in the range of 402 to 507
                    <br />Example: <b>Life Fitness - CT9500 - CT95-0XXXX-18 (S/N 6393097-Up)</b> will have a serial number that is larger than 6393097
                  </div>
                </div>

                <br />
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Precor models might also show display console serial codes
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    If you are looking for a Precor model and see multiple serial codes in the parentheses with the + sign, you are viewing serial codes for the display console or screen of the machine. <b>The first serial code is for the equipment model itself and the following code(s) is for the console.</b>
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: <b>Precor - EFX 825 W/O Cap + PVS (ADFX + ADGZ + AAEY)</b> means that the equipment serial number will start with ADFX while the display console&apos;s serial number will either start with ADGZ or AAEY.
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
        <div className={styles.articleContainer}>
          <div className={styles.articleInner}>
            <h3 className={styles.articleTitle}>After Your Search: Making Sure The Part Is Right</h3>
          </div>
          <div className={styles.tipsContainer}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Make sure the part is used on your exact equipment
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-12' style={{ padding: 15 }}>
                    When you are looking at a part, make sure your exact equipment is in the list under &quot;Used on the following models.&quot;
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Make sure the equipment name is your exact model
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    When you are looking at an equipment name, make sure it is your exact model. Many models have similar numbers and may &quot;look the same&quot; but may represent different versions that may not use all of the same parts! <b>Make sure your part is listed with your model number before ordering!</b>
                  </div>
                  <div className='col-sm-6' style={{ padding: 15 }}>
                    Example: &quot;WETL21020&quot; vs &quot;WETL21021&quot; vs &quot;WETL21022&quot; vs &quot;WETL21023&quot;
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
        <div className={styles.articleContainer}>
          <div className={styles.articleInner}>
            <h3 className={styles.articleTitle}>Other Helpful Information</h3>
          </div>
          <div className={styles.tipsContainer}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Treadbelts
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-12' style={{ padding: 15 }}>
                    You can find a replacement treadbelt with the correct measurements; the length of the belt and the width of the belt are needed. The best way to do this (if the belt is still on the machine) is to take a piece of string and wrap it around the belt and then measure the string.
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Cables
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-12' style={{ padding: 15 }}>
                    We do not recommend trying to find a cable used on another machine that &quot;looks like&quot; it will fit on your machine. The size of the cable may be correct, but the ends may not match up. We recommend you only order the cable if you are sure that it is the right part and are willing to pay the restocking fee if it is not. We do sell pieces to the cable assembly (like stock cable, end pieces, and tools) if you want to make the cables yourself.
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Hardware
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-12' style={{ padding: 15 }}>
                    We do not (yet) classify our parts by the dimensions, so the only way we are able to look up the correct nuts/screws/washers for your machine is by the make/model information or the manufacturer part number.
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                Everything Directional is User-Centric
              </AccordionSummary>
              <AccordionDetails className={styles.tipDetails}>
                <div className='row'>
                  <div className='col-sm-12' style={{ padding: 15 }}>
                    When trying to determine the left from the right side or front from back, etc. we always do so from the user&apos;s point of view. For example: the right side of a treadmill (also known as &quot;user right&quot;) is always the side which is on the right of the user as he/she is walking on the treadmill.
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
        <div className={styles.articleContainer}>
          <div className={styles.articleInner}>
            <h3 className={styles.articleTitle}>Additional Resources</h3>
          </div>
          <div className={styles.tipsContainer}>
            <List>
              <ListItem>
                <ListItemIcon>
                  <SearchIcon />
                </ListItemIcon>
                <ListItemText>
                  Have you tried our <strong><a href='/part_search'>Advanced Search</a></strong>?
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <HelpIcon />
                </ListItemIcon>
                <ListItemText>
                  <strong><a href='/ticket.php?action=open'>Contact Us</a></strong> for direct assistance.
                </ListItemText>
              </ListItem>
            </List>
          </div>
        </div>
    </>
  );
};

export default SearchTips;